import React, { useState } from "react";

export const LEASE_OWN_DATA = [
  { year: 1, net: 2.8, phase: "lease" },
  { year: 2, net: 2.9, phase: "lease" },
  { year: 3, net: 3.0, phase: "lease" },
  { year: 4, net: 3.2, phase: "lease" },
  { year: 5, net: 3.3, phase: "lease" },
  { year: 6, net: 3.4, phase: "lease" },
  { year: 7, net: 3.5, phase: "lease" },
  { year: 8, net: 3.7, phase: "lease" },
  { year: 9, net: 3.8, phase: "lease" },
  { year: 10, net: 4.0, phase: "lease" },
  { year: 11, net: 4.1, phase: "lease" },
  { year: 12, net: 4.3, phase: "lease" },
  { year: 13, net: 11.2, phase: "owned", note: "Includes one-time buyout (₹1L) and inverter replacement (~₹3.5L)" },
  { year: 14, net: 16.2, phase: "owned" },
  { year: 15, net: 16.8, phase: "owned" },
  { year: 16, net: 17.3, phase: "owned" },
  { year: 17, net: 17.9, phase: "owned" },
  { year: 18, net: 18.6, phase: "owned" },
  { year: 19, net: 19.2, phase: "owned" },
  { year: 20, net: 19.9, phase: "owned" },
  { year: 21, net: 20.6, phase: "owned" },
  { year: 22, net: 21.3, phase: "owned" },
  { year: 23, net: 22.0, phase: "owned" },
  { year: 24, net: 22.8, phase: "owned" },
  { year: 25, net: 23.5, phase: "owned" },
];

const PHASE_LABEL = {
  lease: "Lease — ₹0 invested, WattZap owns & operates",
  owned: "Ownership — the plant is yours",
};

export default function LeaseOwnChart({ show, reduced, compact = false }) {
  const data = LEASE_OWN_DATA;
  const cum = [];
  let run = 0;
  data.forEach((d) => { run = +(run + d.net).toFixed(1); cum.push(run); });
  const total = run;

  const W = 880;
  const H = compact ? 250 : 420;
  const mL = compact ? 12 : 50, mR = compact ? 12 : 58, mT = compact ? 26 : 64, mB = compact ? 30 : 44;
  const pw = W - mL - mR, ph = H - mT - mB;
  const MAXB = 25, MAXC = 300;
  const slot = pw / data.length;
  const barW = slot * 0.72;
  const xC = (i) => mL + slot * i + slot / 2;
  const yB = (v) => mT + ph - (v / MAXB) * ph;
  const yC = (v) => mT + ph - (v / MAXC) * ph;
  const divX = mL + slot * 12;
  const glowId = compact ? "loGlowC" : "loGlowF";
  const [tip, setTip] = useState(null);

  const fade = (delay) => ({
    opacity: reduced || show ? 1 : 0,
    transition: reduced ? "none" : `opacity .5s ease ${delay}s`,
  });
  const linePath = `M ${mL},${yC(0)} L ` + data.map((d, i) => `${xC(i)},${yC(cum[i])}`).join(" L ");
  const endX = xC(24), endY = yC(total);

  const svg = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
      aria-label="25-year chart: net savings per year as bars (lease years teal, ownership years green) with cumulative savings line in gold reaching about 2.9 crore rupees"
      data-testid={compact ? "home-leaseown-svg" : "journey-leaseown-svg"}>
      <defs>
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <style>{`
        .lo-halo { transform-box: fill-box; transform-origin: center; animation: loPing 2.6s ease-out infinite; }
        @keyframes loPing { 0% { transform: scale(.6); opacity: .7; } 70% { transform: scale(1.7); opacity: 0; } 100% { opacity: 0; } }
        @media (prefers-reduced-motion: reduce) { .lo-halo { animation: none; opacity: 0; } }
      `}</style>

      {!compact && [10, 20].map((v) => (
        <g key={v}>
          <line x1={mL} y1={yB(v)} x2={W - mR} y2={yB(v)} stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
          <text x={mL - 8} y={yB(v) + 4} textAnchor="end" fontSize="11" fill="rgba(255,255,255,0.5)">{`₹${v}L`}</text>
        </g>
      ))}
      {!compact && [100, 200, 300].map((v) => (
        <text key={v} x={W - mR + 8} y={yC(v) + 4} textAnchor="start" fontSize="11" fill="rgba(255,200,87,0.6)">{`₹${v}L`}</text>
      ))}
      {!compact && (
        <>
          <text x={mL - 38} y={mT - 14} fontSize="10" fill="rgba(255,255,255,0.45)">net ₹L/yr</text>
          <text x={W - 4} y={mT - 14} textAnchor="end" fontSize="10" fill="rgba(255,200,87,0.6)">cumulative ₹L</text>
        </>
      )}

      {/* bars */}
      {data.map((d, i) => (
        <rect key={d.year}
          x={xC(i) - barW / 2} y={yB(d.net)} width={barW} height={yB(0) - yB(d.net)} rx={compact ? 2 : 3}
          fill={d.phase === "lease" ? "rgba(0,212,255,0.75)" : "#21F59B"}
          style={{
            transform: reduced || show ? "scaleY(1)" : "scaleY(0)",
            transformOrigin: "bottom",
            transformBox: "fill-box",
            transition: reduced ? "none" : `transform .45s ease ${i * 0.04}s`,
          }}
        />
      ))}

      {/* cumulative line */}
      <path d={linePath} pathLength="1000" fill="none" stroke="#FFC857"
        strokeWidth={compact ? 2.5 : 3} strokeLinecap="round" strokeLinejoin="round" filter={`url(#${glowId})`}
        style={{
          strokeDasharray: 1000,
          strokeDashoffset: reduced ? 0 : show ? 0 : 1000,
          transition: reduced ? "none" : "stroke-dashoffset 1.4s ease-out 1.05s",
        }}
      />

      {/* Year 12 divider */}
      <g style={fade(1.75)}>
        <line x1={divX} y1={mT - 6} x2={divX} y2={mT + ph} stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeDasharray="5 5" />
        {compact ? (
          <text x={divX + 6} y={mT - 10} fontSize="10.5" fill="rgba(255,255,255,0.75)">Year 12 — ownership</text>
        ) : (
          <>
            <text x={divX + 8} y={mT - 40} fontSize="12" fontWeight="600" fill="#FFFFFF">Year 12 — you take ownership</text>
            <text x={divX + 8} y={mT - 24} fontSize="11" fill="rgba(255,255,255,0.6)">(₹1 lakh, price printed in the contract)</text>
          </>
        )}
      </g>

      {/* Year 13 callout */}
      {!compact && (
        <g style={fade(2.05)}>
          <circle cx={xC(12)} cy={yB(11.2) - 12} r="4.5" fill="#FFC857" />
          <line x1={xC(12) + 4} y1={yB(11.2) - 16} x2={xC(12) + 20} y2={yB(23) + 22} stroke="rgba(255,200,87,0.5)" strokeWidth="1" />
          <text x={xC(12) + 22} y={yB(23) + 8} fontSize="11" fill="#FFC857">One-time costs already included:</text>
          <text x={xC(12) + 22} y={yB(23) + 22} fontSize="11" fill="rgba(255,200,87,0.75)">buyout + new inverter</text>
        </g>
      )}

      {/* endpoint sun + label */}
      <g style={fade(compact ? 2.0 : 2.45)}>
        <circle className="lo-halo" cx={endX} cy={endY} r={compact ? 8 : 11} fill="none" stroke="#FFC857" strokeWidth="2" />
        <circle cx={endX} cy={endY} r={compact ? 5 : 6.5} fill="#FFC857" filter={`url(#${glowId})`} />
        <text x={endX - 16} y={endY - 12} textAnchor="end" fontSize={compact ? 11 : 13} fontWeight="700" fill="#FFC857">
          {compact ? "≈ ₹2.9 Cr" : "≈ ₹2.9 crore total"}
        </text>
      </g>

      {/* x-axis labels */}
      {(compact ? [1, 12, 25] : [1, 5, 10, 12, 15, 20, 25]).map((yr) => (
        <text key={yr} x={xC(yr - 1)} y={mT + ph + 18} textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.55)">{`${yr}`}</text>
      ))}
      <text x={mL} y={mT + ph + (compact ? 18 : 34)} fontSize="10" fill="rgba(255,255,255,0.4)">{compact ? "" : "year"}</text>

      {/* hit areas for tooltip */}
      {!compact && data.map((d, i) => (
        <rect key={`hit-${d.year}`} x={mL + slot * i} y={mT - 6} width={slot} height={ph + 6} fill="transparent"
          style={{ cursor: "pointer" }}
          data-testid={`leaseown-bar-hit-${d.year}`}
          onMouseEnter={() => setTip(i)}
          onMouseLeave={() => setTip(null)}
          onClick={() => setTip(tip === i ? null : i)}
        />
      ))}
    </svg>
  );

  if (compact) return svg;

  return (
    <div className="overflow-x-auto -mx-2 px-2" data-testid="journey-leaseown-chart">
      <div className="relative min-w-[760px]">
        {svg}
        {tip !== null && (
          <div
            data-testid="leaseown-tooltip"
            className="absolute z-20 pointer-events-none bg-[#0A2540] border border-white/20 rounded-lg px-3 py-2 text-xs text-white shadow-xl whitespace-nowrap"
            style={{
              left: `${Math.min(84, Math.max(14, (xC(tip) / W) * 100))}%`,
              top: `${(yB(data[tip].net) / H) * 100}%`,
              transform: "translate(-50%, -112%)",
            }}
          >
            <div className="font-semibold">Year {data[tip].year}</div>
            <div>Net savings ₹{data[tip].net.toFixed(1)}L · Cumulative ₹{cum[tip].toFixed(0)}L</div>
            <div className={data[tip].phase === "lease" ? "text-[#00D4FF]" : "text-[#21F59B]"}>{PHASE_LABEL[data[tip].phase]}</div>
            {data[tip].note && <div className="text-[#FFC857] mt-1 whitespace-normal max-w-[220px]">{data[tip].note}</div>}
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/70" data-testid="journey-leaseown-legend">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#00D4FF]/75" /> Lease years — ₹0 invested, WattZap owns &amp; operates</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#21F59B]" /> Ownership years — the plant is yours</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-1 rounded bg-[#FFC857]" /> Cumulative savings</span>
      </div>
    </div>
  );
}
